import { request } from '../request'

/**
 * 获取链路信息请求参数
 */
export interface GetChainInfoRequest {
  /**
   * 目标安全域
   */
  dstAreaId: string
  /**
   * 源安全域
   */
  srcAreaId: string
  /**
   * 场景
   */
  scene?: string
}

/**
 * 获取链路信息响应数据
 */
export interface GetChainInfoResponse {
  /**
   * 任务ID
   */
  taskId: string
}

/**
 * 获取链路信息
 * @param params 请求参数
 */
export function getChainInfo(params: GetChainInfoRequest) {
  return request(`/portal/v1/tp/area/src/${params.srcAreaId}/dst/${params.dstAreaId}/device`, {
    method: 'GET',
  })
}

/**
 * v1AgentListResponse
 */
export interface GetAgentResponse {
  items?: V1AgentListResponseSecurityDomain[]
  [property: string]: any
}

/**
 * v1AgentListResponseSecurityDomain
 */
export interface V1AgentListResponseSecurityDomain {
  /**
   * 安全域id
   */
  id?: string
  /**
   * 安全域名字
   */
  name?: string
  os?: V1AgentListResponseOSType[]
  [property: string]: any
}

/**
 * v1AgentListResponseOSType
 */
export interface V1AgentListResponseOSType {
  agents?: BasbasCommonv1Agent[]
  osSystem?: BasbasCommonv1OsType
  [property: string]: any
}

/**
 * basbas_commonv1Agent
 */
export interface BasbasCommonv1Agent {
  agentId?: string
  /**
   * agent名称
   */
  agentName?: string
  aliveStatus?: BasCommonv1AliveStatus
  /**
   * 主机名称
   */
  hostName?: string
  ip?: string[]
  [property: string]: any
}

/**
 * bas_commonv1AliveStatus
 */
export enum BasCommonv1AliveStatus {
  AliveStatusFalse = 'AliveStatusFalse',
  AliveStatusTrue = 'AliveStatusTrue',
  AliveStatusUnknown = 'AliveStatusUnknown',
}

/**
 * basbas_commonv1OsType
 */
export enum BasbasCommonv1OsType {
  OsTypeLinux = 'OsTypeLinux',
  OsTypeLinuxUOS = 'OsTypeLinuxUOS',
  OsTypeMac = 'OsTypeMac',
  OsTypeUnknown = 'OsTypeUnknown',
  OsTypeWindows = 'OsTypeWindows',
}

// 获取agent
export function getAgent() {
  return request(`/portal/v1/task/agent_list`, {
    method: 'GET',
  })
}

// 获取模板列表
export interface GetTemplateList {
  /**
   * 模板名字
   */
  'filterParams.name'?: string
  /**
   * 来源类型
   *
   * - OriginTypeSystem: 系统内置
   * - OriginTypeCustom: 手动添加
   */
  'filterParams.originType'?: FilterParamsOriginType
  /**
   * 关联场景ids
   */
  'filterParams.sceneIds'?: number[]
  /**
   * 排序字段
   *
   * - TemplateSortFieldUpdateTime: 更新时间
   * - TemplateSortFieldResourceAmount: 用例数量
   */
  'filterParams.sortField'?: FilterParamsSortField
  /**
   * 排序方式
   *
   * - SortOrderUnspecified: 默认值，未指定排序方式
   * - SortOrderAscending: 正序
   * - SortOrderDescending: 倒序
   */
  'filterParams.sortOrder'?: FilterParamsSortOrder
  /**
   * 验证模式ids
   */
  'filterParams.verificationModeIds'?: number[]
  /**
   * 页码
   */
  page?: number
  /**
   * 页大小
   */
  pageSize?: number
  [property: string]: any
}
/**
 * 来源类型
 *
 * - OriginTypeSystem: 系统内置
 * - OriginTypeCustom: 手动添加
 */
export enum FilterParamsOriginType {
  OriginTypeCustom = 'OriginTypeCustom',
  OriginTypeSystem = 'OriginTypeSystem',
  OriginTypeUnknown = 'OriginTypeUnknown',
}

/**
 * 排序字段
 *
 * - TemplateSortFieldUpdateTime: 更新时间
 * - TemplateSortFieldResourceAmount: 用例数量
 */
export enum FilterParamsSortField {
  TemplateSortFieldResourceAmount = 'TemplateSortFieldResourceAmount',
  TemplateSortFieldUnknown = 'TemplateSortFieldUnknown',
  TemplateSortFieldUpdateTime = 'TemplateSortFieldUpdateTime',
}

/**
 * 排序方式
 *
 * - SortOrderUnspecified: 默认值，未指定排序方式
 * - SortOrderAscending: 正序
 * - SortOrderDescending: 倒序
 */
export enum FilterParamsSortOrder {
  SortOrderAscending = 'SortOrderAscending',
  SortOrderDescending = 'SortOrderDescending',
  SortOrderUnspecified = 'SortOrderUnspecified',
}

export const getTemplateList = (data: GetTemplateList) =>
  request('/portal/common/resourceTemplates', {
    method: 'post',
    data,
  })

export interface GetCaseList {
  /**
   * 攻击类型id
   */
  'filterParams.attackTypeId'?: string
  /**
   * 用例名称
   */
  'filterParams.name'?: string
  /**
   * 来源类型
   *
   * - OriginTypeSystem: 系统内置
   * - OriginTypeCustom: 手动添加
   */
  'filterParams.originType'?: FilterParamsOriginType
  /**
   * 用例分级
   *
   * - ResourceLevelJunior: 初级
   * - ResourceLevelMiddle: 中级
   * - ResourceLevelSenior: 高级
   */
  'filterParams.resourceLevel'?: FilterParamsResourceLevel
  /**
   * 危险等级
   *
   * - RiskLevelLow: 低危
   * - RiskLevelMiddle: 中危
   * - RiskLevelHigh: 高危
   * - RiskLevelGross: 严重
   */
  'filterParams.riskLevel'?: FilterParamsRiskLevel
  /**
   * 排序字段
   */
  'filterParams.sortField'?: FilterParamsSortField
  /**
   * 排序方式
   *
   * - SortOrderUnspecified: 默认值，未指定排序方式
   * - SortOrderAscending: 正序
   * - SortOrderDescending: 倒序
   */
  'filterParams.sortOrder'?: FilterParamsSortOrder
  /**
   * 标签ids
   */
  'filterParams.tagId'?: string[]
  'filterParams.templateId'?: string[]
  /**
   * 验证模式id
   */
  'filterParams.verificationModeId'?: number
  /**
   * 页码
   */
  page?: number
  /**
   * 页大小
   */
  pageSize?: number
  templateId?: string[]
  [property: string]: any
}

/**
 * 来源类型
 *
 * - OriginTypeSystem: 系统内置
 * - OriginTypeCustom: 手动添加
 */

/**
 * 用例分级
 *
 * - ResourceLevelJunior: 初级
 * - ResourceLevelMiddle: 中级
 * - ResourceLevelSenior: 高级
 */
export enum FilterParamsResourceLevel {
  ResourceLevelJunior = 'ResourceLevelJunior',
  ResourceLevelMiddle = 'ResourceLevelMiddle',
  ResourceLevelSenior = 'ResourceLevelSenior',
  ResourceLevelUnknown = 'ResourceLevelUnknown',
}

/**
 * 危险等级
 *
 * - RiskLevelLow: 低危
 * - RiskLevelMiddle: 中危
 * - RiskLevelHigh: 高危
 * - RiskLevelGross: 严重
 */
export enum FilterParamsRiskLevel {
  RiskLevelGross = 'RiskLevelGross',
  RiskLevelHigh = 'RiskLevelHigh',
  RiskLevelLow = 'RiskLevelLow',
  RiskLevelMiddle = 'RiskLevelMiddle',
  RiskLevelUnknown = 'RiskLevelUnknown',
}

// 获取关联用例列表
export const getCaseList = (data: GetCaseList) =>
  request(`/portal/common/resourceTemplate/resources`, {
    method: 'post',
    data,
  })

export interface SaveCaseToTemplate {
  /**
   * 描述
   */
  description?: string
  /**
   * 模板名字
   */
  name?: string
  /**
   * 关联用例ids
   */
  resourceIds?: string[]
  /**
   * 关联场景ids
   */
  sceneIds?: string[]
  /**
   * 验证模式ids
   */
  verificationModeIds?: string[]
  [property: string]: any
}

// 保存已选择用例为模板
export const saveCaseToTemplate = (data: SaveCaseToTemplate) =>
  request(`/portal/common/resourceTemplate`, {
    method: 'post',
    data,
  })

// 定义接口类型
export enum V1ResourceCenterOriginType {
  OriginTypeCustom = 'OriginTypeCustom',
  OriginTypeSystem = 'OriginTypeSystem',
  OriginTypeUnknown = 'OriginTypeUnknown',
}

export interface V1GetResourceTemplateListReplyTemplateItem {
  description?: string
  name?: string
  originType?: V1ResourceCenterOriginType
  resourceAmount?: number
  resourceIds?: string[]
  sceneIds?: number[]
  updateTime?: Date
  verificationModeIds?: number[]
  [property: string]: any
}

export interface TemplateResponse {
  list?: V1GetResourceTemplateListReplyTemplateItem[]
  total?: number
  [property: string]: any
}

export interface GetCaseDetail {
  resourceId: string
  taskId?: string
}

export const getCaseDetail = (data: GetCaseDetail) =>
  request(`/portal/verify/tp/plugins/${data.resourceId}`, {
    method: 'get',
    params: data,
  })

// 获取所有模板id
export const getAllTemplateId = (data: GetTemplateList) =>
  request('/portal/common/resourceTemplates/ids', {
    method: 'post',
    data,
  })

// 获取所有用例id
export const getAllCaseId = (data: GetCaseList) =>
  request('/portal/common/resourceTemplate/filterResourceIds', {
    method: 'post',
    data,
    timeout: 8 * 1000,
  })

// 获取站点分析概览
export const getSiteAnalyzeOverview = () =>
  request('/portal/v1/sites_overview', {
    method: 'get',
  })

// 获取站点设备筛选列表
export const getSiteDeviceFilterList = () =>
  request('/portal/v1/devices_by_site', {
    method: 'get',
  })

// 攻击类型
export const getAttackTypeList = data =>
  request('/portal/common/attack/attack_type_filter_list', {
    method: 'get',
    params: data,
    data,
  })

// 获取用例标签
export const getResourceTagList = data =>
  request('/portal/common/resources/tag/list', {
    method: 'post',
    data,
  })

// 获取创建站点安全设备列表
export const getCreateSiteSecurityDeviceList = () =>
  request('/portal/v1/devices_by_type', {
    method: 'get',
  })

// 根据templateIds获取resourceIds
export const getResourceIdsByTemplateIds = data =>
  request('/portal/common/resourceTemplate/filter/ResourceIds', {
    method: 'post',
    data,
  })

// 获取执行失败用例列表
export const getFailedCaseList = data =>
  request(`/portal/verify/waf/result/${data.resultId}/sentFailedCaseList`, {
    method: 'get',
  })

// 获取未执行用例列表
export const getUnsentCaseList = data =>
  request(`/portal/verify/waf/result/${data.resultId}/unsentCaseList`, {
    method: 'get',
  })

// 获取任务结果日志
export const getTaskResultLog = data =>
  request(`/portal/verify/waf/results/detail/payload/getPayloadLog`, {
    method: 'get',
    params: data,
  })

// 获取验证结果(图表)
export const getVerifyResult = data =>
  request(`/portal/v1/waf/verify_result_distribution`, {
    method: 'get',
    params: data,
  })

// 危险等级分布（图表）
export const getRiskLevelDistribution = data =>
  request(`/portal/v1/waf/risk_level_distribution`, {
    method: 'get',
    params: data,
  })

// 攻击防护排名（图表）
export const getAttackProtectionRanking = data =>
  request(`/portal/v1/waf/attack_protection_ranking`, {
    method: 'get',
    params: data,
  })

// 设备防护能力排名
export const getDeviceProtectionRanking = data =>
  request(`/portal/v1/waf/tasks/${data.resultId}/device_defence`, {
    method: 'get',
  })

// 模板防护失效排名
export const getTemplateProtectionFailureRanking = data =>
  request(`/portal/v1/waf/tasks/${data.resultId}/template_statistic`, {
    method: 'get',
  })

// 用例等级分布
export const getCaseLevelDistribution = data =>
  request(`/portal/v1/waf/tasks/${data.resultId}/resource_level_distribution`, {
    method: 'get',
  })

// 获取用例分级筛选数据
export const getResourceFilterData = data =>
  request(`/portal/v1/waf/tasks/${data.resultId}/filter_data`, {
    method: 'get',
  })

// 创建任务
export const tpCreateTask = data =>
  request('/portal/verify/tp/createTask', {
    method: 'post',
    data,
  })

// 获取邮箱列表
export const getEmail = () => request('/portal/common/email_users')
